import { ComponentProps } from 'react'
import { useGetMeProfile } from '@cocoonspace/sdk-js/v2/domains/me-profile/me-profile'
import { Button } from '@cocoonspace/ui/components/button'
import { Card } from '@cocoonspace/ui/components/card'
import { Skeleton } from '@cocoonspace/ui/components/skeleton'
import { useLingui } from '@lingui/react/macro'
import { ArrowRightIcon, PencilIcon, ShareIcon } from 'lucide-react'
import { TriangleAlertIcon } from 'lucide-react'
import { LocalizedLink as Link } from '~/components/localized-link'

import { ShareSheet } from '~/components/share-sheet'
import { UserAvatar } from '~/components/user-avatar'
import { useAuth } from '~/providers/auth-provider'

const ProfileCard = ({ children }: ComponentProps<typeof Card>) => (
	<Card className='flex flex-1 flex-col gap-2 p-4 md:col-span-2'>
		{children}
	</Card>
)

const CardTitle = () => {
	const { t } = useLingui()

	return (
		<div className='flex flex-col'>
			<div className='font-bold'>{t`Ma page de prise de rendez-vous`}</div>
		</div>
	)
}

const ProfileCardSkeleton = () => (
	<ProfileCard>
		<CardTitle />

		<div className='flex items-center gap-4'>
			<Skeleton className='size-[80px] shrink-0 rounded-full' />

			<div className='flex flex-col gap-2 leading-tight'>
				<Skeleton className='h-7 w-24' />
				<Skeleton className='h-4 w-16' />
			</div>
		</div>

		<div className='flex items-center gap-4 self-end'>
			<Skeleton className='h-[14px] w-16' />
			<Skeleton className='h-[14px] w-16' />
		</div>
	</ProfileCard>
)

const NoProfileCard = () => {
	const { t } = useLingui()

	return (
		<ProfileCard>
			<CardTitle />

			<div className='text-primary flex flex-1 items-center justify-center gap-2 font-semibold'>
				<TriangleAlertIcon />
				<div>{t`Vous n'avez pas encore créé votre page`}</div>
			</div>

			<Button
				variant='link'
				size='sm'
				className='mt-auto ml-auto gap-1'
				asChild
			>
				<Link href='/scheduling'>
					{t`Créer ma page`}
					<ArrowRightIcon className='size-3' />
				</Link>
			</Button>
		</ProfileCard>
	)
}

export const UserProfile = () => {
	const { t } = useLingui()
	const { isAuth, isLoading: isAuthLoading } = useAuth()

	const { data: profile, isLoading } = useGetMeProfile({
		query: {
			enabled: isAuth && !isAuthLoading,
			retry: false,
		},
	})

	if (isAuthLoading || isLoading) return <ProfileCardSkeleton />
	if (!profile?.slug) return <NoProfileCard />

	return (
		<ProfileCard>
			<CardTitle />

			<div className='flex items-center gap-4'>
				<UserAvatar size='xl' />

				<div className='flex flex-col leading-tight'>
					<div className='text-2xl font-bold'>{profile?.title}</div>

					<div className='text-muted-foreground line-clamp-2 text-sm'>
						{profile?.description}
					</div>
				</div>
			</div>

			<div className='flex items-center gap-4 self-end'>
				<Button
					variant='link'
					size='sm'
					className='gap-1'
					asChild
				>
					<Link href='/scheduling'>
						<PencilIcon className='size-3' />
						{t`Modifier`}
					</Link>
				</Button>

				<ShareSheet>
					<Button
						variant='link'
						size='sm'
						className='gap-1'
					>
						<ShareIcon className='size-3' />
						{t`Partager`}
					</Button>
				</ShareSheet>
			</div>
		</ProfileCard>
	)
}
